
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import PasswordValidator from '@/components/admin/users/password-validator.vue'
import {
  resetPasswordWithOtp,
  sendForgotPasswordOtp
} from '@/components/admin/users/users.service'

@Component({
  components: { PasswordValidator }
})
export default class ForgotPasswordCard extends Vue {
  step: 'email' | 'otp' | 'password' = 'email'
  email = ''
  otp = ''
  loading = false
  error: string | null = null
  success: string | null = null
  resendSecondsLeft = 0
  resendTimer: number | null = null

  get title(): string {
    if (this.step === 'otp') return 'Enter code'
    if (this.step === 'password') return 'New password'
    return 'Forgot Password'
  }

  get canSendEmail(): boolean {
    return /.+@.+\..+/.test(this.email)
  }

  get resendDisabled(): boolean {
    return this.resendSecondsLeft > 0 || !this.canSendEmail
  }

  @Watch('step', { immediate: true })
  onStepChanged(step: string) {
    this.$emit('step-change', step)
  }

  beforeDestroy() {
    this.clearResendTimer()
  }

  continueToPassword() {
    this.error = null
    this.success = null
    if (this.otp.length !== 6) return
    this.step = 'password'
  }

  sanitizeOtp() {
    this.otp = String(this.otp || '')
      .replace(/\D/g, '')
      .slice(0, 6)
  }

  startResendCooldown() {
    this.resendSecondsLeft = 60
    this.clearResendTimer()
    this.resendTimer = window.setInterval(() => {
      this.resendSecondsLeft -= 1
      if (this.resendSecondsLeft <= 0) {
        this.clearResendTimer()
      }
    }, 1000)
  }

  clearResendTimer() {
    if (this.resendTimer) {
      window.clearInterval(this.resendTimer)
      this.resendTimer = null
    }
  }

  async sendCode() {
    if (!this.canSendEmail) return
    this.error = null
    this.success = null
    try {
      this.loading = true
      await sendForgotPasswordOtp(this.email)
      this.success = 'A code was sent to your email'
      this.step = 'otp'
      this.startResendCooldown()
    } catch (e) {
      this.error = 'Could not send the code. Try again in a moment.'
    } finally {
      this.loading = false
    }
  }

  async changePassword(payload: { newPassword: string }) {
    this.error = null
    this.success = null
    try {
      this.loading = true
      await resetPasswordWithOtp(this.email, this.otp, payload.newPassword)
      this.$bvToast.toast('Password updated. Sign in with your new password.', {
        title: 'Forgot Password',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-bottom-right'
      })
      this.$emit('done')
    } catch (e) {
      const payloadErr = e?.response?.data
      const message =
        payloadErr?.errors?.[0] ||
        payloadErr?.error ||
        'Could not change the password. Check the code and try again.'
      this.error = message
      if (
        String(message)
          .toLowerCase()
          .includes('otp')
      ) {
        this.step = 'otp'
      }
    } finally {
      this.loading = false
    }
  }
}
